import { useNavigate } from 'react-router-dom';
import { auth, db } from '../config/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useState, useEffect } from 'react';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  verifyBeforeUpdateEmail,
  deleteUser,
  signOut,
  updatePassword as fbUpdatePassword,
} from 'firebase/auth';
import { FaEye, FaEyeSlash, FaCheckCircle, FaRegCircle } from 'react-icons/fa';
import { FiMail, FiMapPin, FiLock, FiEyeOff, FiSave, FiLogOut, FiTrash2, FiAlertCircle, FiX } from 'react-icons/fi';
import { geocode } from '../utils/geocoding';
import { geohashForLocation } from 'geofire-common';
import { getTownAndCountry } from '../utils/geo';
import { doc, deleteDoc, updateDoc, query, collection, where, getDocs } from "firebase/firestore";
import Modal from 'react-modal';
import './userAccount.css';
import useSignoutUser from '../services/useSignout';
import { useDispatch, useSelector } from 'react-redux';
import { setProfileData, clearAppData } from '../redux/reducers/appDataSlice';
import { useSnackbar } from 'notistack';
import { ClipLoader } from 'react-spinners';

export const UserAccount = () => {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const cachedProfile = useSelector((state) => state.appData?.profiles?.[user?.uid] || null);

  const [userDocId, setUserDocId] = useState('');
  const [isStealth, setIsStealth] = useState(cachedProfile ? Boolean(cachedProfile.isStealth) : false);
  const [myIsPremium, setMyIsPremium] = useState(cachedProfile ? !!cachedProfile.isPremium : true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showPremiumErrorModal, setShowPremiumErrorModal] = useState(false);

  // Email confirmation popup & password modal states
  const [showEmailSentModal, setShowEmailSentModal] = useState(false);
  const [showPasswordPromptModal, setShowPasswordPromptModal] = useState(false);
  const [passwordPromptInput, setPasswordPromptInput] = useState('');
  const [pendingNewEmail, setPendingNewEmail] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');
  const [isSendingEmailVerification, setIsSendingEmailVerification] = useState(false);

  // Dedicated Change Email Modal states
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
  const [modalNewEmail, setModalNewEmail] = useState('');
  const [modalCurrentPassword, setModalCurrentPassword] = useState('');
  const [showModalPw, setShowModalPw] = useState(false);
  const [verifyMethod, setVerifyMethod] = useState('password'); // 'password' | 'email'

  const [emailVal, setEmailVal] = useState(user?.email ?? '');
  const [locationVal, setLocationVal] = useState('');
  const [initialLocation, setInitialLocation] = useState('');

  const [currentPasswordVal, setCurrentPasswordVal] = useState('');
  const [newPasswordVal, setNewPasswordVal] = useState('');
  const [confirmPasswordVal, setConfirmPasswordVal] = useState('');

  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const signoutUser = useSignoutUser();

  // Password requirement live checks
  const hasMinLength = newPasswordVal.length >= 8;
  const hasNumber = /\d/.test(newPasswordVal);
  const hasUppercase = /[A-Z]/.test(newPasswordVal);
  const hasSpecialChar = /[^A-Za-z0-9]/.test(newPasswordVal);
  const isPasswordValid = hasMinLength && hasNumber && hasUppercase && hasSpecialChar;
  const passwordsMatch = newPasswordVal === confirmPasswordVal;

  useEffect(() => {
    if (user?.email) {
      if (!emailVal) {
        setEmailVal(user.email);
      }
      const savedPending = localStorage.getItem('pending_email_change_' + user.uid);
      if (savedPending) {
        if (savedPending.toLowerCase() === user.email.toLowerCase()) {
          localStorage.removeItem('pending_email_change_' + user.uid);
          setPendingEmail('');
        } else {
          setPendingEmail(savedPending);
        }
      }
    }
  }, [user, emailVal]);

  const handleConfirmPasswordAndSendEmail = async () => {
    if (!passwordPromptInput || !pendingNewEmail || !user) return;
    setIsSendingEmailVerification(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, passwordPromptInput);
      await reauthenticateWithCredential(user, credential);
      await verifyBeforeUpdateEmail(user, pendingNewEmail);

      setPendingEmail(pendingNewEmail);
      if (user?.uid) {
        localStorage.setItem('pending_email_change_' + user.uid, pendingNewEmail);
      }
      setShowPasswordPromptModal(false);
      setPasswordPromptInput('');
      setEmailVal(user.email);
      setShowEmailSentModal(true);
      enqueueSnackbar('Confirmation email sent!', { variant: 'success' });
    } catch (err) {
      console.error('Email change error:', err);
      enqueueSnackbar(err.message || 'Failed to send confirmation email. Check your password.', { variant: 'error' });
    } finally {
      setIsSendingEmailVerification(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!pendingEmail || !user) return;
    setIsSendingEmailVerification(true);
    try {
      await verifyBeforeUpdateEmail(user, pendingEmail);
      setShowEmailSentModal(true);
      enqueueSnackbar('Confirmation email re-sent!', { variant: 'success' });
    } catch (err) {
      console.error('Resend error:', err);
      enqueueSnackbar(err.message || 'Failed to resend confirmation email. Please try again.', { variant: 'error' });
    } finally {
      setIsSendingEmailVerification(false);
    }
  };

  const handleCloseAllModals = () => {
    setShowEmailSentModal(false);
    setShowChangeEmailModal(false);
    setShowPasswordPromptModal(false);
    setModalNewEmail('');
    setModalCurrentPassword('');
  };

  const handleSubmitModalEmailChange = async () => {
    if (!modalNewEmail || !user) return;
    const targetEmail = modalNewEmail.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(targetEmail)) {
      enqueueSnackbar('Please enter a valid email address.', { variant: 'warning' });
      return;
    }
    if (targetEmail.toLowerCase() === user.email.toLowerCase()) {
      enqueueSnackbar('New email must be different from your current email.', { variant: 'warning' });
      return;
    }

    if (verifyMethod === 'password' && !modalCurrentPassword) {
      enqueueSnackbar('Please enter your current password.', { variant: 'warning' });
      return;
    }

    setIsSendingEmailVerification(true);
    try {
      if (verifyMethod === 'password') {
        const credential = EmailAuthProvider.credential(user.email, modalCurrentPassword);
        await reauthenticateWithCredential(user, credential);
      }
      await verifyBeforeUpdateEmail(user, targetEmail);

      setPendingEmail(targetEmail);
      if (user?.uid) {
        localStorage.setItem('pending_email_change_' + user.uid, targetEmail);
      }
      setEmailVal(user.email);
      // Opens confirmation modal on top of change email modal
      setShowEmailSentModal(true);
      enqueueSnackbar('Confirmation email sent successfully!', { variant: 'success' });
    } catch (err) {
      console.error('Email change error:', err);
      let errMsg = err.message || 'Failed to send confirmation email.';
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        errMsg = 'Incorrect current password. Please try again.';
      } else if (err.code === 'auth/email-already-in-use') {
        errMsg = 'This email address is already in use by another account.';
      } else if (err.code === 'auth/requires-recent-login') {
        errMsg = 'Recent login required. Please choose "Verify by Password" and enter your password.';
        setVerifyMethod('password');
      }
      enqueueSnackbar(errMsg, { variant: 'error' });
    } finally {
      setIsSendingEmailVerification(false);
    }
  };

  useEffect(() => {
    if (!user) return;

    const fetchUserData = async () => {
      try {
        const userQuery = query(collection(db, 'users'), where('authUID', '==', user.uid));
        const querySnapshot = await getDocs(userQuery);
        if (!querySnapshot.empty) {
          const userDoc = querySnapshot.docs[0];
          setUserDocId(userDoc.id);
          const data = userDoc.data();
          dispatch(setProfileData({ uid: user.uid, data }));
          setIsStealth(Boolean(data.isStealth));
          setMyIsPremium(!!data.isPremium);
          if (data.location && data.location.latitude && data.location.longitude) {
            getTownAndCountry(data.location.latitude, data.location.longitude).then(locationName => {
              if (locationName) {
                const place = locationName.town ? locationName.town.trim() : "";
                const country = locationName.country ? locationName.country.trim() : "";
                const locStr = place && country ? `${place}, ${country}` : (place || country);
                if (locStr) {
                  setLocationVal(locStr);
                  setInitialLocation(locStr);
                }
              }
            });
          }
        }
      } catch (err) {
        console.error('fetchUserProfile error:', err);
        setMyIsPremium(false);
      }
    };
    fetchUserData();
  }, [user, dispatch]);

  const toggleInvisibleMode = async () => {
    if (!user) return;
    if (!myIsPremium) {
      setShowUpgradeModal(true);
      return;
    }
    const nextStealth = !isStealth;
    setIsStealth(nextStealth);

    try {
      if (userDocId) {
        await updateDoc(doc(db, 'users', userDocId), { isStealth: nextStealth });
      }
    } catch (error) {
      console.error('Error updating Invisible Mode:', error);
      setIsStealth(!nextStealth);
    }
  };

  const handleSaveChanges = async (e) => {
    if (e) e.preventDefault();
    if (!user) return;

    setIsSaving(true);
    let hasUpdates = false;

    try {
      // 1. Update Location if changed
      if (locationVal && locationVal !== initialLocation && userDocId) {
        const locationData = await geocode(locationVal);
        if (!locationData) {
          enqueueSnackbar('Could not find that location. Please try again.', { variant: 'error' });
          setIsSaving(false);
          return;
        }
        const { latitude, longitude } = locationData;
        const geohash = geohashForLocation([latitude, longitude], 9);
        await updateDoc(doc(db, 'users', userDocId), {
          location: { latitude, longitude, geohash },
        });
        setInitialLocation(locationVal);
        hasUpdates = true;
      }

      // 2. Update Password if new password entered
      if (newPasswordVal) {
        if (!isPasswordValid) {
          enqueueSnackbar('Password must be at least 8 characters, contain a number, an uppercase letter, and a special character.', { variant: 'warning' });
          setIsSaving(false);
          return;
        }
        if (!passwordsMatch) {
          enqueueSnackbar('New password and Confirm new password do not match.', { variant: 'warning' });
          setIsSaving(false);
          return;
        }

        let authPassword = currentPasswordVal;
        if (!authPassword) {
          authPassword = window.prompt('Please enter your current password to confirm password change:');
          if (!authPassword) {
            enqueueSnackbar('Current password is required to change password.', { variant: 'warning' });
            setIsSaving(false);
            return;
          }
        }

        const credential = EmailAuthProvider.credential(user.email, authPassword);
        await reauthenticateWithCredential(user, credential);
        await fbUpdatePassword(user, newPasswordVal);

        setCurrentPasswordVal('');
        setNewPasswordVal('');
        setConfirmPasswordVal('');
        hasUpdates = true;
      }

      // 3. Update Email if changed
      if (emailVal && emailVal.trim().toLowerCase() !== user.email.toLowerCase()) {
        const targetEmail = emailVal.trim();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(targetEmail)) {
          enqueueSnackbar('Please enter a valid email address.', { variant: 'warning' });
          setIsSaving(false);
          return;
        }

        let authPassword = currentPasswordVal;
        if (!authPassword) {
          setPendingNewEmail(targetEmail);
          setShowPasswordPromptModal(true);
          setIsSaving(false);
          return;
        }

        const credential = EmailAuthProvider.credential(user.email, authPassword);
        await reauthenticateWithCredential(user, credential);
        await verifyBeforeUpdateEmail(user, targetEmail);

        setPendingEmail(targetEmail);
        if (user?.uid) {
          localStorage.setItem('pending_email_change_' + user.uid, targetEmail);
        }
        setShowEmailSentModal(true);
        setEmailVal(user.email);
        hasUpdates = true;
      }

      if (hasUpdates) {
        enqueueSnackbar('Account settings updated successfully!', { variant: 'success' });
      } else {
        enqueueSnackbar('No changes to save.', { variant: 'info' });
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      enqueueSnackbar(err.message || 'Failed to update settings. Please try again.', { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const deleteAccount = async () => {
    if (!user) return;
    if (myIsPremium) {
      setShowPremiumErrorModal(true);
      return;
    }
    setShowDeleteConfirmModal(true);
  };

  const confirmDeleteAccount = async () => {
    setShowDeleteConfirmModal(false);
    const password = window.prompt('Please enter your current password to confirm deletion:');
    if (!password) {
      enqueueSnackbar('Password is required to reauthenticate.', { variant: 'warning' });
      return;
    }

    setIsDeleting(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);

      if (userDocId) {
        await deleteDoc(doc(db, 'users', userDocId));
      }
      await deleteUser(user);
      dispatch(clearAppData());
      await signOut(auth);
      enqueueSnackbar('Account successfully deleted.', { variant: 'info' });
      navigate('/login', { replace: true });
    } catch (err) {
      console.error('Error deleting account:', err);
      enqueueSnackbar(err.message || 'Failed to delete account.', { variant: 'error' });
      setIsDeleting(false);
    }
  };

  const logOut = async () => {
    try {
      dispatch(clearAppData());
      await signoutUser();
      navigate('/login', { replace: true });
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  return (
    <>
      <Modal
        className="upgrade-prompt"
        isOpen={showUpgradeModal}
        onRequestClose={() => setShowUpgradeModal(false)}
        contentLabel="Upgrade to unlock Invisible Mode"
      >
        <h2>Invisible Mode is Premium</h2>
        <p>Go invisible to browse without appearing online and without logging your profile visits.</p>
        <p>Upgrade to Premium to enable this feature.</p>
        <div className="actions">
          <button onClick={() => setShowUpgradeModal(false)}>Maybe later</button>
          <button
            onClick={() => {
              setShowUpgradeModal(false);
              navigate('/pricing');
            }}
          >
            Upgrade
          </button>
        </div>
      </Modal>

      <Modal
        className="upgrade-prompt"
        isOpen={showDeleteConfirmModal}
        onRequestClose={() => setShowDeleteConfirmModal(false)}
        contentLabel="Confirm Account Deletion"
      >
        <h2>Are you sure?</h2>
        <p>This action is permanent and cannot be undone.</p>
        <div className="actions">
          <button onClick={() => setShowDeleteConfirmModal(false)}>Cancel</button>
          <button
            onClick={confirmDeleteAccount}
            className="delete-button"
          >
            Delete
          </button>
        </div>
      </Modal>

      <Modal
        className="upgrade-prompt"
        isOpen={showPremiumErrorModal}
        onRequestClose={() => setShowPremiumErrorModal(false)}
        contentLabel="Premium Account Deletion Error"
      >
        <h2>Premium Account</h2>
        <p>Please cancel your subscription before deleting your account.</p>
        <div className="actions">
          <button onClick={() => setShowPremiumErrorModal(false)}>OK</button>
        </div>
      </Modal>

      {/* Email Confirmation Sent Modal */}
      <Modal
        className="email-confirm-modal"
        overlayClassName="email-confirm-overlay"
        isOpen={showEmailSentModal}
        onRequestClose={handleCloseAllModals}
        contentLabel="Email Confirmation Sent"
        ariaHideApp={false}
      >
        <div className="email-modal-icon-badge">
          <FiMail />
        </div>
        <h2 className="email-modal-title">Confirmation Email Sent</h2>
        <p className="email-modal-text">
          A confirmation link has been sent to your email to verify changing your address to:
        </p>
        <div className="email-modal-target-box">
          <span>{pendingEmail}</span>
        </div>
        <p className="email-modal-instruction">
          Please check your inbox. Your email address will change once you click the link to confirm in that email.
        </p>
        <div className="email-modal-validity-pill">
          <span className="validity-icon">⏱️</span>
          <span className="validity-text">
            Valid for: <strong>1 hour</strong>
          </span>
        </div>
        <div className="email-modal-actions">
          <button
            type="button"
            className="email-modal-btn-confirm"
            onClick={handleCloseAllModals}
          >
            OK
          </button>
        </div>
      </Modal>

      {/* Password Authorization Modal */}
      <Modal
        className="email-confirm-modal password-reauth-modal"
        overlayClassName="email-confirm-overlay"
        isOpen={showPasswordPromptModal}
        onRequestClose={() => {
          setShowPasswordPromptModal(false);
          setPasswordPromptInput('');
        }}
        contentLabel="Confirm Password to Change Email"
        ariaHideApp={false}
      >
        <div className="email-modal-icon-badge">
          <FiLock />
        </div>
        <h2 className="email-modal-title">Confirm Password</h2>
        <p className="email-modal-text">
          For your security, please enter your current password to authorize sending the email change confirmation.
        </p>
        <div className="cream-input-container" style={{ margin: '14px 0', width: '100%' }}>
          <input
            type="password"
            className="cream-input"
            placeholder="Current Password"
            value={passwordPromptInput}
            onChange={(e) => setPasswordPromptInput(e.target.value)}
            autoFocus
          />
        </div>
        <div className="email-modal-actions two-buttons">
          <button
            type="button"
            className="email-modal-btn-cancel"
            onClick={() => {
              setShowPasswordPromptModal(false);
              setPasswordPromptInput('');
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="email-modal-btn-confirm"
            onClick={handleConfirmPasswordAndSendEmail}
            disabled={!passwordPromptInput || isSendingEmailVerification}
          >
            {isSendingEmailVerification ? <ClipLoader size={16} color="#1A1A1A" /> : 'Confirm'}
          </button>
        </div>
      </Modal>

      {/* Dedicated Change Email Modal (matches requested UI) */}
      <Modal
        className="email-dialog-card-modal"
        overlayClassName="email-dialog-card-overlay"
        isOpen={showChangeEmailModal}
        onRequestClose={() => {
          setShowChangeEmailModal(false);
          setModalNewEmail('');
          setModalCurrentPassword('');
        }}
        contentLabel="Change Email Address"
        ariaHideApp={false}
      >
        <div className="change-email-modal-header">
          <div className="change-email-header-left">
            <div className="email-badge-circle">
              <FiMail />
            </div>
            <div className="header-text-block">
              <h3 className="change-email-title">Email Address</h3>
              <p className="change-email-subtitle">Update your email address</p>
            </div>
          </div>
          <button
            type="button"
            className="btn-modal-close"
            onClick={() => {
              setShowChangeEmailModal(false);
              setModalNewEmail('');
              setModalCurrentPassword('');
            }}
            aria-label="Close"
          >
            <FiX />
          </button>
        </div>

        {/* Current Email Address */}
        <div className="modal-input-group">
          <label className="modal-field-label">Current Email Address</label>
          <div className="cream-input-container">
            <input
              type="email"
              className="cream-input"
              value={user?.email || ''}
              readOnly
            />
          </div>
          <span className="modal-helper-text">Your currently registered email address</span>
        </div>

        {/* New Email Address */}
        <div className="modal-input-group">
          <label className="modal-field-label">New Email Address</label>
          <div className="cream-input-container">
            <input
              type="email"
              className="cream-input"
              placeholder="Enter your new email address"
              value={modalNewEmail}
              onChange={(e) => setModalNewEmail(e.target.value)}
              autoFocus
            />
          </div>
          <span className="modal-helper-text">A verification link will be sent to your new email.</span>
        </div>

        {/* Verification Method Selection: Password or Email */}
        <div className="modal-input-group">
          <label className="modal-field-label">Verification Method</label>
          <div className="verify-method-selector">
            <button
              type="button"
              className={`verify-method-pill ${verifyMethod === 'password' ? 'active' : ''}`}
              onClick={() => setVerifyMethod('password')}
            >
              <FiLock className="pill-icon" />
              <span>Verify by Password</span>
            </button>
            <button
              type="button"
              className={`verify-method-pill ${verifyMethod === 'email' ? 'active' : ''}`}
              onClick={() => setVerifyMethod('email')}
            >
              <FiMail className="pill-icon" />
              <span>Verify by Email</span>
            </button>
          </div>

          {verifyMethod === 'password' ? (
            <div className="password-auth-subgroup">
              <label className="modal-field-label" style={{ marginTop: '12px' }}>Current Password</label>
              <div className="cream-input-container">
                <input
                  type={showModalPw ? 'text' : 'password'}
                  className="cream-input"
                  placeholder="Enter current password"
                  value={modalCurrentPassword}
                  onChange={(e) => setModalCurrentPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="eye-icon-button"
                  onClick={() => setShowModalPw(!showModalPw)}
                  tabIndex={-1}
                >
                  {showModalPw ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              <span className="modal-helper-text">Enter your password to authorize this change.</span>
            </div>
          ) : (
            <div className="email-auth-info-card">
              <FiMail className="info-icon" />
              <span>A confirmation link will be sent to authorize this change.</span>
            </div>
          )}
        </div>

        <button
          type="button"
          className="btn-modal-change-email-submit"
          onClick={handleSubmitModalEmailChange}
          disabled={!modalNewEmail || (verifyMethod === 'password' && !modalCurrentPassword) || isSendingEmailVerification}
        >
          {isSendingEmailVerification ? (
            <ClipLoader size={18} color="#1A1A1A" />
          ) : (
            <>
              <FiMail style={{ marginRight: 8, fontSize: '1.05rem' }} />
              <span>Change Email</span>
            </>
          )}
        </button>
        <p className="modal-bottom-footnote">
          Your email address will be updated after verification.
        </p>
      </Modal>

      <div className="main settings user-account">
        <div className="user-account-wrapper">
          {/* Header */}
          <div className="account-header">
            <h1>Account Settings</h1>
            <p className="subtitle">Manage your account details and preferences</p>
          </div>

          <form onSubmit={handleSaveChanges}>
            {/* 1. Email Address Section */}
            <div className="settings-group">
              <div className="section-title-row email-title-row">
                <div className="icon-badge">
                  <FiMail />
                </div>
                <div className="section-text">
                  <span className="section-title">Email Address</span>
                  <span className="section-desc">Update your email address</span>
                </div>
                <button
                  type="button"
                  className="btn-change-email-trigger"
                  onClick={() => {
                    setModalNewEmail('');
                    setModalCurrentPassword('');
                    setShowChangeEmailModal(true);
                  }}
                >
                  Change Email
                </button>
              </div>
              <div className="section-content-indented">
                <div
                  className="cream-input-container"
                  onClick={() => {
                    setModalNewEmail('');
                    setModalCurrentPassword('');
                    setShowChangeEmailModal(true);
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <input
                    type="email"
                    className="cream-input"
                    value={emailVal || user?.email || ''}
                    readOnly
                    placeholder="allancesar@yahoo.com"
                    style={{ cursor: 'pointer' }}
                  />
                </div>
                {pendingEmail && (
                  <div className="pending-email-notice">
                    <div className="pending-email-header">
                      <span className="pending-badge">Verification Pending</span>
                      <span className="valid-time">Valid for 1 hr</span>
                    </div>
                    <p className="pending-desc">
                      Confirmation link sent for <strong>{pendingEmail}</strong>. The email will change once confirmed.
                    </p>
                    <div className="pending-actions">
                      <button
                        type="button"
                        className="btn-resend-link"
                        onClick={handleResendConfirmation}
                      >
                        Resend confirmation
                      </button>
                      <button
                        type="button"
                        className="btn-cancel-pending"
                        onClick={() => {
                          setPendingEmail('');
                          if (user) localStorage.removeItem('pending_email_change_' + user.uid);
                        }}
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 2. Location Section */}
            <div className="settings-group">
              <div className="section-title-row">
                <div className="icon-badge">
                  <FiMapPin />
                </div>
                <div className="section-text">
                  <span className="section-title">Location</span>
                  <span className="section-desc">Update your location</span>
                </div>
              </div>
              <div className="section-content-indented">
                <div className="cream-input-container">
                  <input
                    type="text"
                    className="cream-input"
                    value={locationVal}
                    onChange={(e) => setLocationVal(e.target.value)}
                    placeholder="Horsham, United Kingdom"
                  />
                </div>
              </div>
            </div>

            {/* 3. Password Section */}
            <div className="settings-group">
              <div className="section-title-row">
                <div className="icon-badge">
                  <FiLock />
                </div>
                <div className="section-text">
                  <span className="section-title">Password</span>
                  <span className="section-desc">Choose a strong password</span>
                </div>
              </div>

              <div className="section-content-indented">
                {/* Current Password Stacked Box */}
                <div className="cream-input-container-stacked">
                  <label className="input-inner-label">Current Password</label>
                  <input
                    type={showCurrentPw ? 'text' : 'password'}
                    className="cream-input-stacked"
                    value={currentPasswordVal}
                    onChange={(e) => setCurrentPasswordVal(e.target.value)}
                    placeholder="••••••••••••"
                  />
                  <button
                    type="button"
                    className="cream-input-icon"
                    onClick={() => setShowCurrentPw(!showCurrentPw)}
                    aria-label="Toggle password visibility"
                  >
                    {showCurrentPw ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>

                {/* New Password Stacked Box */}
                <div className="cream-input-container-stacked">
                  <label className="input-inner-label">New Password</label>
                  <input
                    type={showNewPw ? 'text' : 'password'}
                    className="cream-input-stacked"
                    value={newPasswordVal}
                    onChange={(e) => setNewPasswordVal(e.target.value)}
                    placeholder="••••••••••••"
                  />
                  <button
                    type="button"
                    className="cream-input-icon"
                    onClick={() => setShowNewPw(!showNewPw)}
                    aria-label="Toggle password visibility"
                  >
                    {showNewPw ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>

                {/* Requirement Checklist - Single Horizontal Row */}
                <div className="password-requirements">
                  <span className="req-label">Your password must include:</span>
                  <div className="req-items">
                    <div className={`req-item ${hasMinLength ? 'valid' : ''}`}>
                      {hasMinLength ? <FaCheckCircle className="req-icon valid" /> : <FaRegCircle className="req-icon" />}
                      <span>8+ characters</span>
                    </div>
                    <div className={`req-item ${hasNumber ? 'valid' : ''}`}>
                      {hasNumber ? <FaCheckCircle className="req-icon valid" /> : <FaRegCircle className="req-icon" />}
                      <span>One number</span>
                    </div>
                    <div className={`req-item ${hasUppercase ? 'valid' : ''}`}>
                      {hasUppercase ? <FaCheckCircle className="req-icon valid" /> : <FaRegCircle className="req-icon" />}
                      <span>One uppercase letter</span>
                    </div>
                    <div className={`req-item ${hasSpecialChar ? 'valid' : ''}`}>
                      {hasSpecialChar ? <FaCheckCircle className="req-icon valid" /> : <FaRegCircle className="req-icon" />}
                      <span>One special character</span>
                    </div>
                  </div>
                </div>

                {/* Confirm New Password Stacked Box */}
                <div className="cream-input-container-stacked">
                  <label className="input-inner-label">Confirm New Password</label>
                  <input
                    type={showConfirmPw ? 'text' : 'password'}
                    className="cream-input-stacked"
                    value={confirmPasswordVal}
                    onChange={(e) => setConfirmPasswordVal(e.target.value)}
                    placeholder="••••••••••••"
                  />
                  <button
                    type="button"
                    className="cream-input-icon"
                    onClick={() => setShowConfirmPw(!showConfirmPw)}
                    aria-label="Toggle password visibility"
                  >
                    {showConfirmPw ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {confirmPasswordVal && !passwordsMatch && (
                  <span style={{ color: '#ff6b6b', fontSize: '0.76rem', marginTop: 4, display: 'block' }}>
                    Passwords do not match
                  </span>
                )}

                {/* Save Changes Button */}
                <div style={{ marginTop: 24 }}>
                  <button
                    type="submit"
                    className="btn-primary-save"
                    disabled={isSaving}
                  >
                    <FiSave style={{ fontSize: 16 }} />
                    <span>{isSaving ? 'Saving...' : 'Save Changes'}</span>
                  </button>
                </div>
              </div>
            </div>
          </form>

          {/* 4. Invisible Mode Section (Placed below Save Changes with generous padding) */}
          <div className="settings-group" style={{ marginTop: 44, marginBottom: 24 }}>
            <div className="stealth-row">
              <div className="stealth-left">
                <div className="icon-badge">
                  <FiEyeOff />
                </div>
                <div className="section-text">
                  <span className="section-title">Invisible Mode</span>
                  <span className="section-desc">
                    When enabled, you won't appear on the discover page and your profile visits won't be recorded.
                  </span>
                </div>
              </div>
              <label className="ios-switch">
                <input
                  type="checkbox"
                  checked={isStealth}
                  onChange={toggleInvisibleMode}
                />
                <span className="ios-slider"></span>
              </label>
            </div>
          </div>

          {/* Additional Actions: Sign Out */}
          <div className="account-actions" style={{ marginTop: 0 }}>
            <div className="or-divider">
              <span>or</span>
            </div>

            <button
              type="button"
              className="btn-secondary-signout"
              onClick={logOut}
            >
              <FiLogOut style={{ fontSize: 16 }} />
              <span>Sign out of account</span>
            </button>
          </div>

          {/* 5. Delete Account Section */}
          <div className="delete-account-card">
            <div className="delete-account-header-row">
              <div className="icon-badge">
                <FiTrash2 />
              </div>
              <div className="section-text">
                <h2 className="delete-account-title">Delete Account</h2>
                <p className="delete-account-desc">
                  Deleting your account will permanently remove all of your information, including your profile, messages and photos. This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="delete-account-alert-box">
              <FiAlertCircle className="delete-alert-icon" />
              <div className="delete-alert-text">
                <p className="alert-line-1">This will permanently delete your account and all data.</p>
                <p className="alert-line-2">This action cannot be undone.</p>
              </div>
            </div>

            <button
              type="button"
              className="btn-delete-account"
              onClick={deleteAccount}
            >
              <FiTrash2 style={{ fontSize: 16 }} />
              <span>Yes, delete my account</span>
            </button>
          </div>
        </div>
      </div>

      {isDeleting && (
        <div className="loading-overlay">
          <div className="loader-container">
            <ClipLoader size={50} color="#FBEAD3" />
            <p>Deleting your account...</p>
          </div>
        </div>
      )}
    </>
  );
};
